import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Fab,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";

const API_BASE_URL = process.env.REACT_APP_SERVERURL || "";

const getStoredCustomer = () => {
  try {
    const customer =
      JSON.parse(localStorage.getItem("customer")) ||
      JSON.parse(localStorage.getItem("user")) ||
      null;

    return customer;
  } catch {
    return null;
  }
};

const CustomerChatbot = ({
  clientId,
  restaurantName = "Restaurant",
  brandConfig = {},
}) => {
  const primaryColor = brandConfig?.primaryColor || "#1976d2";

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(
    localStorage.getItem("customer_chat_session_id") || ""
  );
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const customer = getStoredCustomer();
  const customerId =
    customer?.id ||
    customer?.customer_id ||
    localStorage.getItem("customer_id") ||
    "";

  const finalClientId =
    clientId ||
    localStorage.getItem("client_id") ||
    process.env.REACT_APP_CLIENT_ID ||
    "";

  const chatUrl = `${API_BASE_URL}/api/v1/restaurant/${finalClientId}/customer-chat/`;

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, open]);

  const addMessage = (role, text, extra = {}) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        role,
        text,
        created_at: new Date().toISOString(),
        ...extra,
      },
    ]);
  };

  const sendMessage = async (customMessage) => {
    const msg = customMessage || message.trim();
    if (!msg || loading || !finalClientId) return;

    addMessage("user", msg);
    setMessage("");
    setLoading(true);

    try {
      const payload = {
        message: msg,
      };

      if (sessionId) payload.session_id = sessionId;
      if (customerId) payload.customer_id = customerId;

      const res = await fetch(chatUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || data?.message || "Chat request failed");
      }

      if (data?.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("customer_chat_session_id", data.session_id);
      }

      addMessage("bot", data?.reply || "I received your message.", {
        action_taken: data?.action_taken,
        action_result: data?.action_result,
        created_at: data?.created_at,
      });
    } catch (error) {
      addMessage(
        "bot",
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!sessionId || !finalClientId) return;

    setHistoryLoading(true);

    try {
      const res = await fetch(`${chatUrl}${sessionId}/history/`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Unable to load chat history");
      }

      const history = Array.isArray(data)
        ? data
        : data?.messages || data?.results || [];

      const formatted = history.map((item) => ({
        id: item.id || Date.now() + Math.random(),
        role:
          item.role ||
          item.sender ||
          (item.is_customer || item.message_type === "user" ? "user" : "bot"),
        text: item.message || item.content || item.reply || "",
        created_at: item.created_at,
        action_taken: item.action_taken,
        action_result: item.action_result,
      }));

      setMessages(formatted);
    } catch (error) {
      addMessage("bot", error.message || "Could not load chat history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const deleteSession = async () => {
    if (!sessionId || !finalClientId) return;

    try {
      await fetch(`${chatUrl}sessions/${sessionId}/`, {
        method: "DELETE",
      });

      localStorage.removeItem("customer_chat_session_id");
      setSessionId("");
      setMessages([]);
      addMessage("bot", "Chat session deleted. You can start a new chat now.");
    } catch {
      addMessage("bot", "Unable to delete this chat session.");
    }
  };

  const quickMessages = [
    "What pizzas do you have?",
    "What is the status of my last order?",
    "Show my recent orders",
    "I want to place an order",
  ];

  return (
    <>
      {!open && (
        <Fab
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: 1500,
            bgcolor: primaryColor,
            color: "#fff",
            "&:hover": { bgcolor: primaryColor },
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            right: { xs: 8, sm: 24 },
            bottom: { xs: 8, sm: 24 },
            width: { xs: "calc(100vw - 16px)", sm: 390 },
            height: { xs: "calc(100vh - 24px)", sm: 570 },
            zIndex: 1500,
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: primaryColor,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography fontWeight={700}>Customer Chat</Typography>
              <Typography variant="caption">
                {restaurantName} support assistant
              </Typography>
            </Box>

            <Box>
              <Tooltip title="Load History">
                <IconButton
                  onClick={loadHistory}
                  disabled={!sessionId || historyLoading}
                  sx={{ color: "#fff" }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Session">
                <IconButton
                  onClick={deleteSession}
                  disabled={!sessionId}
                  sx={{ color: "#fff" }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>

              <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "#f7f7f7",
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              size="small"
              label={sessionId ? "Session active" : "New session"}
              color={sessionId ? "success" : "default"}
            />
            {customerId && (
              <Chip size="small" label="Customer context enabled" />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              bgcolor: "#fafafa",
            }}
          >
            {messages.length === 0 && (
              <Box textAlign="center" mt={4}>
                <SmartToyIcon sx={{ fontSize: 44, color: primaryColor }} />
                <Typography fontWeight={700} mt={1}>
                  How can I help you?
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Ask about menu, orders, payments, or cancel an order.
                </Typography>

                <Stack spacing={1} mt={2}>
                  {quickMessages.map((item) => (
                    <Button
                      key={item}
                      variant="outlined"
                      size="small"
                      onClick={() => sendMessage(item)}
                      sx={{ textTransform: "none" }}
                    >
                      {item}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}

            {messages.map((msg) => {
              const isUser = msg.role === "user";

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "82%",
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isUser ? primaryColor : "#fff",
                      color: isUser ? "#fff" : "text.primary",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Stack direction="row" spacing={0.7} alignItems="center">
                      {isUser ? (
                        <PersonIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <SmartToyIcon sx={{ fontSize: 16 }} />
                      )}
                      <Typography variant="caption" fontWeight={700}>
                        {isUser ? "You" : "Assistant"}
                      </Typography>
                    </Stack>

                    <Typography variant="body2" mt={0.5}>
                      {msg.text}
                    </Typography>

                    {msg.action_taken && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" fontWeight={700}>
                          Action: {msg.action_taken}
                        </Typography>
                      </>
                    )}

                    {msg.action_result && (
                      <Typography variant="caption" display="block">
                        {msg.action_result}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}

            {(loading || historyLoading) && (
              <Box display="flex" justifyContent="center" mt={1}>
                <CircularProgress size={22} />
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            sx={{
              p: 1.5,
              display: "flex",
              gap: 1,
              bgcolor: "#fff",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!message.trim() || loading}
              sx={{
                minWidth: 46,
                bgcolor: primaryColor,
                "&:hover": { bgcolor: primaryColor },
              }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default CustomerChatbot;